// reportPayment.js
import moment from 'moment';
import { select } from '../shared/utils/dom';
import { delimiters } from '../shared/constants';
import { reportPayment, getSelectedCustomerAccountId, removeErrors } from './api';
import { createError, removeError } from '../shared/formErrors';
import loadingMixin from '../shared/vue/mixins/loading';
import multistepMixin from '../shared/vue/mixins/multistep';
import formMixin from '../shared/vue/mixins/form';
import { formatBasicCurrency } from '../shared/utils/misc';
import { regex } from '../shared/constants/validation';

/**
 * Enrolling in auto pay
 */
export default function vueInit() {
  const el = select('[data-vue-app="report"]');
  if (el) {
    const app = new Vue({ // eslint-disable-line no-unused-vars
      el,
      delimiters,
      mixins: [loadingMixin, formMixin, multistepMixin],
      data: {
        account: '',
        accountNumber: '',
        agree: false,
        // for accessibility in live regions
        liveText: '',
        payFromText: '',
        paymentDate: moment().format('MMMM Do, YYYY'),
        paymentAmount: '',
        paymentType: '',
        paymentMethod: '',
        receiptNumber: '',
        shutOffAmount: 0,
        errorMessage: null,
        success: false,
        showWarningMessage: false,
        showSuccessMessagePerCode: false,
        showErrorMessagePerCode: false,
        maxReceiptLength: '',
      },
      /**
       * Computed
       */
      computed: {
        formattedPaymentAmount() {
          return formatBasicCurrency(this.paymentAmount);
        },
      },
      mounted() {
        // see if there's a shut off amount
        this.shutOffAmount = this.$el.dataset.shutOffAmount;
        this.accountNumber = getSelectedCustomerAccountId();
        if (window.fabricator) this.accountNumber = '1234567890';
        this.maxReceiptLength = this.accountNumber.length === 10 ? '15' : '16'; // maxlength of input field determined if account is CIS or DIS
      },
      watch: {
        step(newVal, oldVal) { // eslint-disable-line no-unused-vars
          /**
           * Changes live text depending on the form step
           */
          if (this.step === 2) {
            this.liveText = 'Moving to Step 2 to confirm details of your payment';
          } else if (this.step === 1) {
            this.liveText = 'You are now back on Step 1 to set up your payment';
          }
        },
        date(newVal, oldVal) { // eslint-disable-line no-unused-vars
          // if the date has been updated, set the live text to read the date
          // because Pikaday wasn't reading the date right, and we want to make
          // sure this is accessible.
          if (newVal) {
            this.liveText = `${newVal} is the selected date`;
          }
        },
      },
      methods: {
        handleClickOnContinue(e) {
          // when the user clicks on continue button, validate that the required fields are filled
          // if empty, show error
          // if there are no errors, let the user proceed
          e.preventDefault();
          let hasError = false;

          /*
          * Step 1 validation
          */

          // validate payment amount
          if (!this.paymentAmount) {
            createError(this.$refs.paymentAmount, this.$refs.paymentAmountError, 'Please specify an amount.');
            hasError = true;
          } else {
            removeError(this.$refs.paymentAmount, this.$refs.paymentAmountError);
          }

          // validate receipt number
          if (!this.receiptNumber) {
            createError(this.$refs.receiptNumber, this.$refs.receiptNumberError, 'Please add the receipt number of your payment.');
            hasError = true;
          } else if (!regex.alphanumeric.test(this.receiptNumber)) {
            createError(this.$refs.receiptNumber, this.$refs.receiptNumberError, 'Please enter a valid receipt number.');
            hasError = true;
          } else {
            removeError(this.$refs.receiptNumber, this.$refs.receiptNumberError);
          }

          if (hasError) {
            // update live text to let the user know there are errors
            this.liveText = 'The form has errors. Please make sure your payment information is correct.';
          } else {
            this.nextStep(e);
            // make sure to show the warning message on step 2 if the payment is insufficient to avoid shutoff
            if (this.paymentAmount !== '' && this.paymentAmount > 0 && this.shutOffAmount > 0 && (parseFloat(this.paymentAmount) < parseFloat(this.shutOffAmount))) {
              this.showWarningMessage = true;
            } else {
              this.showWarningMessage = false;
            }
          }
        },
        postFormContents() {
          reportPayment.reportPayment(this)
            .then(() => {
              if (!this.showWarningMessage) {
                this.success = true;
              }
              this.setStep(4);
              this.loading = false;
              Vue.nextTick(() => {
                const paymentAmountVariable = select('#report-payment');
                if (paymentAmountVariable) {
                  paymentAmountVariable.innerHTML = paymentAmountVariable.innerHTML.replace("paymentAmount", `$${this.paymentAmount}`);
                }
              });
            })
            .catch((error) => {
              this.loading = false;
              if (error.response.status === 400 && error.response.data.error.code === 2020) {
                removeErrors();
                this.showSuccessMessagePerCode = true;
                this.showErrorMessagePerCode = false;
                if (!this.showWarningMessage) {
                  this.success = true;
                }
                this.setStep(4);
                Vue.nextTick(() => {
                  const paymentAmountVariable = select('#report-payment');
                  if (paymentAmountVariable) {
                    paymentAmountVariable.innerHTML = paymentAmountVariable.innerHTML.replace("paymentAmount", `$${this.paymentAmount}`);
                  }
                });
              } else if (error.response.status === 400 && error.response.data.error.code === 2001) {
                removeErrors();
                this.showSuccessMessagePerCode = false;
                this.showErrorMessagePerCode = true;
                this.success = false;
              } else {
                this.success = false;
              }
            });
        },
      },
    });
  }
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/reportPayment.js